import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoWarning } from "react-icons/io5";

import { AuthConsentCheckbox } from "@/features/auth/components/AuthConsentCheckbox";
import { AuthEntryCard } from "@/features/auth/components/AuthEntryCard";
import { Button } from "@/shared/components/ui/Button";
import { registrationService } from "@/features/auth/services/registration.service";
import {
  resetRegistration,
  setCorrelationId,
} from "@/features/auth/store/registration.store";
import { useResources } from "@/features/resources/hooks/useResources";
import { useResourceUrl } from "@/features/resources/hooks/useResourceUrl";
import type { Resource } from "@/features/resources/types";

function findDocument(
  resources: Resource[],
  keywords: string[],
): Resource | undefined {
  return resources.find((r) =>
    keywords.some((kw) => r.name.toLowerCase().includes(kw.toLowerCase())),
  );
}

export function AuthRegisterConsentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("register");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeDocument, setActiveDocument] = useState<
    "terms" | "privacy" | null
  >(null);
  const [activeUrl, setActiveUrl] = useState<string | null>(null);

  const { resources } = useResources("onboarding", "document");
  const { getUrl } = useResourceUrl();

  const termsResource = findDocument(resources, [
    "término",
    "terms",
    "condicion",
    "conditions",
  ]);
  const privacyResource = findDocument(resources, [
    "política",
    "policy",
    "protecci",
    "datos",
    "protection",
    "personal",
  ]);

  useEffect(() => {
    resetRegistration();
  }, []);

  async function handleOpenDocument(type: "terms" | "privacy") {
    const resource = type === "terms" ? termsResource : privacyResource;
    if (!resource) return;

    const url = await getUrl(resource.id);
    if (url) {
      setActiveUrl(url);
      setActiveDocument(type);
    }
  }

  async function handleContinue() {
    if (!acceptTerms || !acceptPrivacy) {
      setShowErrors(true);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const result = await registrationService.startRegistration({
      termsAccepted: true,
    });

    setIsLoading(false);

    if (!result.success) {
      setErrorMessage(result.message);
      return;
    }

    if (result.data.correlationId) {
      setCorrelationId(result.data.correlationId);
    }

    navigate("/register/country");
  }

  const termsErrorVisible = showErrors && !acceptTerms;
  const privacyErrorVisible = showErrors && !acceptPrivacy;

  return (
    <>
      <AuthEntryCard variant="register">
        <section className="mt-10 w-full max-w-[22rem] text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
            {t("register:consent.title")}
          </h1>
          <p className="mt-3.5 text-[0.8125rem] leading-6 text-text-muted">
            {t("register:consent.description")}
          </p>
        </section>

        <div className="mt-10 flex w-full max-w-96 flex-col gap-4 text-left text-[0.8125rem]">
          <div className="flex flex-col gap-2.5">
            <AuthConsentCheckbox
              checked={acceptTerms}
              description={
                <>
                  {t("register:consent.terms.label")}{" "}
                  <button
                    className="border-0 bg-transparent p-0 text-inherit underline cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => void handleOpenDocument("terms")}
                    type="button"
                    disabled={!termsResource}
                  >
                    {t("register:consent.terms.link")}
                  </button>
                </>
              }
              id="accept-terms"
              onChange={(checked) => {
                setAcceptTerms(checked);
                if (checked) setShowErrors(false);
              }}
            />
            {termsErrorVisible ? (
              <div
                className="flex min-h-10 items-center gap-3 rounded-[0.625rem] bg-[#fde9eb] px-3.5 py-2.5 text-sm leading-[1.35] text-[#2f3640]"
                role="alert"
              >
                <span
                  aria-hidden="true"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.45rem]"
                >
                  <IoWarning size="2.75rem" color="#ff3b4f" />
                </span>
                <span>{t("register:consent.error.terms")}</span>
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-2.5">
            <AuthConsentCheckbox
              checked={acceptPrivacy}
              description={
                <>
                  {t("register:consent.privacy.label")}{" "}
                  <button
                    className="border-0 bg-transparent p-0 text-inherit underline cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => void handleOpenDocument("privacy")}
                    type="button"
                    disabled={!privacyResource}
                  >
                    {t("register:consent.privacy.link")}
                  </button>
                </>
              }
              id="accept-privacy"
              onChange={(checked) => {
                setAcceptPrivacy(checked);
                if (checked) setShowErrors(false);
              }}
            />
            {privacyErrorVisible ? (
              <div
                className="flex min-h-10 items-center gap-3 rounded-[0.625rem] bg-[#fde9eb] px-3.5 py-2.5 text-sm leading-[1.35] text-[#2f3640]"
                role="alert"
              >
                <span
                  aria-hidden="true"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.45rem]"
                >
                  <IoWarning size="2.75rem" color="#ff3b4f" />
                </span>
                <span>{t("register:consent.error.privacy")}</span>
              </div>
            ) : null}
          </div>
        </div>

        {errorMessage ? (
          <p
            className="mt-4 flex w-full max-w-96 items-center rounded-[0.625rem] bg-[#fde9eb] px-3.5 py-2.5 text-sm leading-[1.35] text-[#2f3640]"
            role="alert"
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="mt-8 w-full max-w-96">
          <Button
            className="min-h-9 px-5 py-1.5 text-sm"
            disabled={isLoading}
            onClick={() => void handleContinue()}
          >
            {isLoading
              ? t("register:consent.btn.loading")
              : t("register:consent.btn.create")}
          </Button>
          <p className="mt-4 text-center text-[0.8125rem] leading-6 text-text-primary">
            {t("register:consent.login.question")}{" "}
            <Link
              className="text-brand-primary no-underline hover:underline"
              to="/login"
            >
              {t("register:consent.login.action")}
            </Link>
          </p>
        </div>
      </AuthEntryCard>

      {activeDocument && activeUrl ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            aria-label={t("register:consent.modal.close")}
            className="absolute inset-0 bg-[rgba(5,20,31,0.55)]"
            onClick={() => {
              setActiveDocument(null);
              setActiveUrl(null);
            }}
            type="button"
          />
          <div className="relative z-10 flex h-[min(90vh,46rem)] w-full max-w-4xl flex-col rounded-md bg-white shadow-xl">
            <button
              aria-label={t("register:consent.modal.close")}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-soft bg-white text-xl leading-none text-text-muted transition-colors duration-150 hover:border-brand-primary hover:text-brand-primary"
              onClick={() => {
                setActiveDocument(null);
                setActiveUrl(null);
              }}
              type="button"
            >
              ×
            </button>
            <h2 className="px-6 pb-4 pt-6 pr-16 text-lg font-semibold leading-6 text-text-primary">
              {activeDocument === "terms"
                ? t("register:consent.terms.link")
                : t("register:consent.privacy.link")}
            </h2>
            <div className="min-h-0 flex-1 px-6 pb-6">
              <iframe
                src={`${activeUrl}#toolbar=0`}
                className="h-full min-h-[24rem] w-full rounded-md border border-border-soft"
                title={
                  activeDocument === "terms"
                    ? t("register:consent.terms.link")
                    : t("register:consent.privacy.link")
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
